<template>
  <v-card>
    <v-container fluid class="pb-0">
      <p class="text-subtitle-1 font-weight-medium">Select Items</p>
      <InputGeneric label="Type"
        ><v-autocomplete v-model="type" :items="typeItems" dense></v-autocomplete
      ></InputGeneric>
      <InputGeneric label="Source"
        ><v-autocomplete
          v-model="source"
          :items="sourceItems"
          dense
          placeholder="All"
          clearable
        ></v-autocomplete
      ></InputGeneric>
      <InputGeneric label="Connected Node">
        <v-autocomplete
          v-model="node"
          :items="nodeItems"
          dense
          placeholder="All"
          clearable
        ></v-autocomplete>
      </InputGeneric>
      <v-divider class="mb-4"></v-divider>
      <p class="text-subtitle-1 font-weight-medium">Properties</p>
      <v-row
        v-for="(property, i) in affectedProperties"
        :key="property.key"
        dense
        justify="space-between"
      >
        <v-col cols="12" md="8">
          <v-autocomplete
            :value="property.key"
            dense
            :items="propertiesItems"
            @input="updateProperty($event, i, 'key')"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="3" v-if="property.key">
          <Pass v-slot="{ schema }" :schema="schema[property.key]">
            <InputFromSchema
              :value="property.value"
              :schema="schema"
              @input="updateProperty($event, i, 'value')"
            >
            </InputFromSchema>
          </Pass>
        </v-col>
        <v-col cols="12" md="1">
          <v-btn icon @click="removeProperty(i)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-btn rounded block text small color="primary" class="mb-2" @click="addProperty">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-divider></v-divider>
      <v-expansion-panels flat>
        <v-expansion-panel>
          <v-expansion-panel-header class="pl-0">
            <template #actions>
              <v-icon class="icon">mdi-menu-down</v-icon>
            </template>
            <span class="header">Summary</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="text-caption">
            <v-card outlined flat class="pa-1">
              <p class="mb-0">{{ `Items affected (total: ${affectedItems.length}):` }}</p>
              <ul>
                <li v-for="item in affectedItems" :key="item.id">
                  {{ `${item.name} (id: ${item.id})` }}
                </li>
              </ul>
              <p class="mb-0 mt-2">
                {{ `Properties affected (total: ${affectedProperties.length}):` }}
              </p>
              <ul>
                <li v-for="prop in affectedProperties" :key="prop.key">
                  <Pass v-slot="{ schema }" :schema="schema[prop.key]">
                    {{ schema.title }}: {{ prop.value }} {{ schema.unit }}
                  </Pass>
                </li>
              </ul>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="error" text @click="cancel"> cancel </v-btn>
      <v-btn color="primary" text type="submit" @click="save"> save </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { CaseModel, schemas } from "data-dictionary";

const renewableSchema = schemas.find((v) => v.$id === "generator_renewable.schema.json").properties;
const thermalSchema = {
  ...schemas.find((v) => v.$id === "generator_thermal.schema.json").properties,
  ...schemas
    .find((v) => v.$id === "generator_thermal.schema.json")
    .allOf.map((v) => v.then.properties)
    .reduce((obj, item) => ({ ...obj, ...item }), {}),
};

export default {
  name: "MultipleEdit",
  components: {
    InputFromSchema: () => import("@/components/inputs/InputFromSchema.vue"),
    InputGeneric: () => import("@/components/inputs/InputGeneric.vue"),
    Pass: () => import("@/components/Pass.vue"),
  },
  mixins: [],
  props: {
    input: {
      type: Object,
      required: true,
    },
    settings: { type: Object, required: true },
    resourceType: { type: String, required: false, default: "generators" },
  },
  data() {
    return {
      dialog: false,
      selectionMethod: "id",
      type: "THERMAL",
      source: "",
      node: "",
      typeItems: CaseModel.Generator.TYPES,
      sources: CaseModel.Generator.SOURCES,
      affectedProperties: [],
    };
  },
  computed: {
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
    sourceItems() {
      return this.sources[this.type];
    },
    nodeItems() {
      return this.input.nodes.map((v) => ({ text: v.name, value: v.id }));
    },
    affectedItems() {
      return this.input[this.resourceType].filter((v) => {
        let result = v.type === this.type;
        if (this.source) {
          result = result && v.source === this.source;
        }
        if (this.node) {
          result = result && v.bus_id === this.node;
        }
        return result;
      });
    },
    schema() {
      return { THERMAL: thermalSchema, RENEWABLE: renewableSchema }[this.type];
    },
    propertiesItems() {
      return Object.entries(this.schema)
        .filter(
          ([key, value]) =>
            !value.yearly &&
            ["number", "boolean"].includes(value.type) &&
            value.title &&
            !value.title.includes("rofile") &&
            !["name"].includes(key)
        )
        .map(([key, val]) => ({ ...val, key, value: key, text: val.title }))
        .sort((a, b) => {
          const titleA = a.title.toUpperCase();
          const titleB = b.title.toUpperCase();
          if (titleA > titleB) {
            return 1;
          }
          if (titleA < titleB) {
            return -1;
          }

          return 0;
        });
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    addProperty() {
      this.affectedProperties.push({ key: "", value: "" });
    },
    updateProperty($e, index, objKey) {
      if (objKey === "value") {
        const schema = this.schema[this.affectedProperties[index].key];
        let value = $e;
        if (schema.type === "number") {
          value = Number($e);
        }
        this.affectedProperties[index].value = value;
      } else if (objKey === "key") {
        this.affectedProperties[index].key = $e;
        // Fill with default
        const { default: defaultVal } = this.schema[$e];
        this.affectedProperties[index].value = defaultVal || "";
      }
    },
    removeProperty(i) {
      this.affectedProperties.splice(i, 1);
    },
    cancel() {
      this.affectedProperties = [];
      this.type = "THERMAL";
      this.source = "";
      this.node = "";
      this.dialog = false;
      this.$emit("close");
    },
    save() {
      const inputCopy = this.input;
      this.affectedItems.forEach((item) => {
        const itemIndex = inputCopy[this.resourceType].findIndex((v) => v.id === item.id);
        inputCopy[this.resourceType].splice(itemIndex, 1, {
          ...item,
          ...this.affectedProperties.reduce(
            (acc, prop) => ({ ...acc, [prop.key]: prop.value }),
            {}
          ),
        });
      });
      this.computedInput = inputCopy;
      this.cancel();
    },
  },
};
</script>

<style>
.icon {
  order: 0;
}

.header {
  order: 1;
}
</style>
